import { useEffect, useMemo, useRef, useState } from "react";
import { getSessionStorageItem } from "presentation/utils/sessionStorage";
import {
  DataTable,
  DataTableFilterEvent,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import Spinner from "presentation/components/Shared/Spinner";
import {
  translateRegexMessage,
  translateText,
} from "infra/translation/translate";
import { useFilterConfigurationStore } from "presentation/hooks/use-filter-configuration-store";
import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from "infra/axios/axios-api";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Page, PAGE_TYPES } from "domain/entities/Page";
import {
  useCreatePage,
  useDeletePage,
  usePages,
  useRenamePage,
} from "presentation/hooks/use-query-website";

type SortOrder = "asc" | "desc" | "";

function base64url(str: string) {
  const b64 = btoa(unescape(encodeURIComponent(str)));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const WebsiteConfigurations: React.FC = () => {
  const toast = useRef<Toast>(null);

  const { filters, setFilters } = useFilterConfigurationStore();

  const [lazyParams, setLazyParams] = useState({
    sortBy: "name",
    sortOrder: "asc" as SortOrder,
    limit: 50,
    page: 1,
    filters: {} as Record<string, unknown>,
  });

  const {
    data: Pages,
    isLoading,
    isError,
    error,
    refetch,
  } = usePages(PAGE_TYPES.website);

  const createMut = useCreatePage();
  const renameMut = useRenamePage();
  const deleteMut = useDeletePage();

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  type StepStatus = "pending" | "done" | "error";
  type Step = { key: string; label: string; status: StepStatus };

  const [isCreating, setIsCreating] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  const EDIT_ORIGIN =
    window.location.hostname === "localhost"
      ? "http://localhost:5173"
      : "https://www.nimber.pt";

  const initSteps = (configName: string) => {
    setSteps([
      {
        key: "createPage",
        label: `Criando página "${configName}"`,
        status: "pending",
      },
      {
        key: "createSections",
        label: "Criando seções (Header, Content, Footer)",
        status: "pending",
      },
      {
        key: "createHeaderComponents",
        label: "Criando componentes do Header",
        status: "pending",
      },
      {
        key: "createContentComponents",
        label: "Criando componentes do Content",
        status: "pending",
      },
      {
        key: "createFooterComponents",
        label: "Criando componentes do Footer",
        status: "pending",
      },
      { key: "finalizing", label: "Finalizando", status: "pending" },
    ]);
  };

  const setStepStatus = (key: string, status: StepStatus) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));

  const progress = steps.length
    ? Math.round(
        (steps.filter((s) => s.status === "done").length / steps.length) * 100
      )
    : 0;

  const doStep = async (key: string, fn: () => Promise<void>) => {
    try {
      await fn();
      setStepStatus(key, "done");
    } catch (e) {
      setStepStatus(key, "error");
      throw e;
    }
  };

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if (
              typeof value === "object" &&
              value !== null &&
              "constraints" in value
            ) {
              return [
                key,
                Array.isArray(value.constraints)
                  ? value.constraints[0]?.value ?? ""
                  : "",
              ];
            }
            return [key, value?.value ?? ""];
          })
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  useEffect(() => {
    if (isError && toast.current) {
      toast.current.show({
        severity: "error",
        summary: "Erro",
        detail:
          translateRegexMessage((error as Error)?.message, "pt") ||
          "Ocorreu um erro inesperado",
        life: 2000,
      });
    }
  }, [isError, error]);

  const listFiltered = useMemo(() => {
    const list = Pages ?? [];
    const nameFilter = String(lazyParams.filters?.["name"] ?? "").toLowerCase();
    if (!nameFilter) return list;
    return list.filter((p: any) =>
      (p.name ?? "").toLowerCase().includes(nameFilter)
    );
  }, [Pages, lazyParams.filters]);

  const listSorted = useMemo(() => {
    const { sortBy, sortOrder } = lazyParams;
    if (!sortBy || !sortOrder) return listFiltered;

    const arr = [...listFiltered];
    arr.sort((a, b) => {
      const av = (a as any)?.[sortBy];
      const bv = (b as any)?.[sortBy];

      if (av == null && bv == null) return 0;
      if (av == null) return sortOrder === "asc" ? -1 : 1;
      if (bv == null) return sortOrder === "asc" ? 1 : -1;

      if (typeof av === "string" && typeof bv === "string") {
        return sortOrder === "asc"
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }

      const result = av < bv ? -1 : av > bv ? 1 : 0;
      return sortOrder === "asc" ? result : -result;
    });

    return arr;
  }, [listFiltered, lazyParams]);

  const pageData = listSorted.slice(
    (lazyParams.page - 1) * lazyParams.limit,
    lazyParams.page * lazyParams.limit
  );

  const totalRecords = listSorted.length;

  const onDelete = (row: Page) => {
    confirmDialog({
      message: `Tem certeza que deseja excluir "${row.name}"?`,
      header: translateText("Confirm deletion", "pt"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: translateText("Delete", "pt"),
      rejectLabel: translateText("Cancel", "pt"),
      accept: () =>
        deleteMut.mutate(
          { id: row.id },
          {
            onSuccess: () =>
              toast.current?.show({
                severity: "success",
                summary: "Excluído",
                detail: `"${row.name}" foi removido.`,
                life: 1500,
              }),
            onError: (e: any) =>
              toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: e?.message ?? "Falha ao excluir.",
                life: 1500,
              }),
          }
        ),
    });
  };

  const qc = useQueryClient();

  const createSection = async (pageId: string, name: string, cols = 0) => {
    const { data } = await httpClient.post(`/section`, {
      data: { pageId, name, cols },
    });
    return data?.data;
  };

  const createComponent = async (input: {
    sectionId: string;
    name: string;
    type: "text" | "image";
    value: string | Record<string, unknown>;
    order: number;
    position?: string;
  }) => {
    const { data } = await httpClient.post(
      `/component`,
      { data: input },
      { timeout: 20000 }
    );
    return data?.data;
  };

  const setStepLabel = (key: string, label: string) =>
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, label } : s)));

  async function createInBatches(
    sectionId: string,
    defs: Array<{
      name: string;
      type: "text" | "image";
      value: any;
      order: number;
      position: string;
    }>,
    stepKey: string,
    batchSize = 3,
    pauseMs = 120
  ) {
    const total = defs.length;
    let done = 0;

    for (let i = 0; i < defs.length; i += batchSize) {
      const batch = defs.slice(i, i + batchSize);

      await Promise.all(batch.map((d) => createComponent({ sectionId, ...d })));

      done += batch.length;
      setStepLabel(
        stepKey,
        `${
          steps.find((s) => s.key === stepKey)?.label.split(" (")[0]
        } (${done}/${total})`
      );

      if (pauseMs > 0 && i + batchSize < defs.length) {
        await new Promise((r) => setTimeout(r, pauseMs));
      }
    }
  }

  const toSlug = (name: string) =>
    name.trim().toLowerCase().replace(/\s+/g, "-");

  const pageRouteMapping: Record<string, string> = {
    Home: "home",
    Consulting: "it-consulting",
    TailorMade: "tailor-made-project",
    Contact: "contact",
    Nearshore: "nearshore",
    AboutUs: "about-us",
    Technologies: "technologies",
    Blog: "blog",
    Careers: "careers",
  };

  const predefinedPages = [
    { name: "Home", route: "home" },
    { name: "Consulting", route: "it-consulting" },
    { name: "TailorMade", route: "tailor-made-project" },
    { name: "Contact", route: "contact" },
    { name: "Nearshore", route: "nearshore" },
    { name: "AboutUs", route: "about-us" },
    { name: "Technologies", route: "technologies" },
    { name: "Blog", route: "blog" },
  ];

  const createPredefinedPage = async (pageName: string) => {
    setNewName(pageName);
    await onCreate();
  };

  const onCreate = async () => {
    const n = newName.trim();
    if (!n) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um nome para a configuração.",
        life: 1500,
      });
      return;
    }

    setIsCreating(true);
    initSteps(n);

    createMut.mutate(
      { name: n, type: PAGE_TYPES.website },
      {
        onSuccess: async (res: any) => {
          try {
            setStepStatus("createPage", "done");

            const pageCreated = res?.data ?? res;
            const pageId: string = pageCreated?.id;
            if (!pageId) throw new Error("ID da página não retornado.");

            const slug = toSlug(n);

            // 1) sections
            await doStep("createSections", async () => {
              const sectionHeader = await createSection(
                pageId,
                `${n}_Header`,
                0
              );
              const sectionContent = await createSection(
                pageId,
                `${n}_Content`,
                0
              );
              const sectionFooter = await createSection(
                pageId,
                `${n}_Footer`,
                0
              );

              const headerId: string = sectionHeader?.id;
              const contentId: string = sectionContent?.id;
              const footerId: string = sectionFooter?.id;

              if (!headerId || !contentId || !footerId) {
                throw new Error("Falha ao criar seções");
              }

              // defaults Header
              const headerDefaults = [
                {
                  name: `${n}_Header_TXT01`,
                  type: "text" as const,
                  value: n,
                  order: 1,
                  position: "",
                },
                {
                  name: `${n}_Header_TXT02`,
                  type: "text" as const,
                  value: `Configuration page for ${n}`,
                  order: 2,
                  position: "",
                },
                {
                  name: `${n}_Header_IMG01`,
                  type: "image" as const,
                  value: `/website/${slug}.png`,
                  order: 10,
                  position: "",
                },
              ];

              // defaults Content
              const contentDefaults = [
                {
                  name: `${n}_Content_TXT01`,
                  type: "text" as const,
                  value: `About ${n}`,
                  order: 1,
                  position: "",
                },
                {
                  name: `${n}_Content_TXT02`,
                  type: "text" as const,
                  value: `This is the configuration page for ${n}. Here you can manage all the settings and options related to this component.`,
                  order: 2,
                  position: "",
                },
                {
                  name: `${n}_Content_TXT03`,
                  type: "text" as const,
                  value: "Settings",
                  order: 3,
                  position: "",
                },
              ];

              // defaults Footer
              const footerDefaults = [
                {
                  name: `${n}_Footer_TXT01`,
                  type: "text" as const,
                  value: "© 2025 Configuration Panel",
                  order: 1,
                  position: "",
                },
                {
                  name: `${n}_Footer_TXT02`,
                  type: "text" as const,
                  value: "All rights reserved",
                  order: 2,
                  position: "",
                },
              ];

              await doStep("createHeaderComponents", async () => {
                await createInBatches(
                  headerId,
                  headerDefaults,
                  "createHeaderComponents",
                  3
                );
              });

              await doStep("createContentComponents", async () => {
                await createInBatches(
                  contentId,
                  contentDefaults,
                  "createContentComponents",
                  3
                );
              });

              await doStep("createFooterComponents", async () => {
                await createInBatches(
                  footerId,
                  footerDefaults,
                  "createFooterComponents",
                  3
                );
              });
            });

            await doStep("finalizing", async () => {
              setNewName("");
              await qc.invalidateQueries({ queryKey: ["website-pages"] });
            });

            toast.current?.show({
              severity: "success",
              summary: "Criado",
              detail: `Configuração "${n}" criada com Header, Content e Footer padrão.`,
              life: 1800,
            });
          } catch (e: any) {
            toast.current?.show({
              severity: "error",
              summary: "Erro ao criar defaults",
              detail: e?.message ?? "Falha ao criar seção/componentes padrão.",
              life: 2500,
            });
          } finally {
            setIsCreating(false);
          }
        },
        onError: (e: any) => {
          setStepStatus("createPage", "error");
          setIsCreating(false);
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao criar a página.",
            life: 2000,
          });
        },
      }
    );
  };

  const onSaveRename = (row: Page) => {
    const nn = editingName.trim();
    if (!nn) {
      toast.current?.show({
        severity: "warn",
        summary: "Nome obrigatório",
        detail: "Informe um novo nome.",
        life: 1500,
      });
      return;
    }
    renameMut.mutate(
      { id: row.id, name: nn },
      {
        onSuccess: () => {
          setEditingId(null);
          toast.current?.show({
            severity: "success",
            summary: "Atualizado",
            detail: `Nome alterado para "${nn}".`,
            life: 1500,
          });
        },
        onError: (e: any) =>
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: e?.message ?? "Falha ao salvar.",
            life: 1500,
          }),
      }
    );
  };

  const goEdit = (row: Page) => {
    const route = pageRouteMapping[row.name] || toSlug(row.name ?? "");
    const raw = getSessionStorageItem("auth-session-storage");
    if (!raw) {
      alert("Sem sessão válida para editar.");
      return;
    }
    const parsed = JSON.parse(raw);
    const st = parsed?.state ?? {};
    if (!st.accessToken) {
      alert("Sem token para editar.");
      return;
    }
    const payload = {
      accessToken: st.accessToken,
      refreshToken: st.refreshToken,
      expiresAt: st.expiresAt,
      userPermissions: st.userPermissions,
    };
    const tok = base64url(JSON.stringify(payload));

    const url = `${EDIT_ORIGIN}/edit/${encodeURIComponent(route)}#tok=${tok}`;

    // console.log("url");
    // console.log(url);

    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">{translateText("Website Configurations", "pt")}</h2>
      </div>

      <div className="card mb-4">
        <h4 className="mb-3">{translateText("Quick Create Pages", "pt")}</h4>
        <p className="text-600 mb-3">
          {translateText("Create standard website pages quickly", "pt")}
        </p>
        <div className="flex flex-wrap gap-2">
          {predefinedPages.map((page) => {
            const exists = Pages?.some((p) => p.name === page.name);
            return (
              <button
                key={page.route}
                className={`p-button p-component p-button-sm ${
                  exists ? "p-button-success" : "p-button-outlined"
                }`}
                onClick={() => !exists && createPredefinedPage(page.name)}
                disabled={createMut.isPending || isCreating || exists}
                title={
                  exists ? "Page already exists" : `Create ${page.name} page`
                }
              >
                <i
                  className={`${exists ? "pi pi-check" : "pi pi-plus"} mr-2`}
                />
                {page.name}
                {exists && <span className="ml-2 text-xs">(✓)</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Formulário manual */}
      <div className="flex align-items-center justify-content-between mb-3">
        <h4 className="m-0">{translateText("Custom Page", "pt")}</h4>

        <div className="flex gap-2">
          <span className="p-input-icon-left">
            <input
              className="p-inputtext p-component"
              style={{ minWidth: 280 }}
              placeholder={translateText("New configuration", "pt")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreate();
              }}
            />
          </span>
          <button
            className="p-button p-component p-button-sm"
            onClick={onCreate}
            disabled={createMut.isPending || isCreating}
          >
            {createMut.isPending || isCreating ? (
              translateText("Saving…", "pt")
            ) : (
              <i className="pi pi-plus" />
            )}
          </button>
        </div>
      </div>

      <DataTable
        value={pageData}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={totalRecords}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: String(e.sortField ?? "name"),
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        rowsPerPageOptions={[10, 25, 50]}
        dataKey="id"
        header={null}
      >
        <Column
          field="name"
          header={translateText("Name", "pt")}
          sortable
          filter
          showFilterMatchModes={false}
          body={(row: Page) =>
            editingId === row.id ? (
              <input
                className="p-inputtext p-component w-full"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
              />
            ) : (
              row.name
            )
          }
        />

        <Column
          header={translateText("Route", "pt")}
          body={(row: Page) =>
            pageRouteMapping[row.name] || toSlug(row.name ?? "")
          }
          style={{ width: "18rem" }}
        />

        <Column
          field="createdAt"
          header={translateText("Created at", "pt")}
          sortable
          body={(row: Page) =>
            row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          field="modifiedAt"
          header={translateText("Updated at", "pt")}
          sortable
          body={(row: Page) =>
            row.modifiedAt ? new Date(row.modifiedAt).toLocaleString() : "-"
          }
          style={{ width: "14rem" }}
        />

        <Column
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
          body={(row: Page) => (
            <div className="flex gap-2 justify-content-center">
              {editingId === row.id ? (
                <>
                  <button
                    className="p-button p-component p-button-sm"
                    onClick={() => onSaveRename(row)}
                    disabled={renameMut.isPending}
                  >
                    {renameMut.isPending
                      ? translateText("Saving…", "pt")
                      : translateText("Save", "pt")}
                  </button>
                  <button
                    className="p-button p-button-text p-component p-button-sm"
                    onClick={() => setEditingId(null)}
                  >
                    {translateText("Cancel", "pt")}
                  </button>
                </>
              ) : (
                <>
                  <i
                    className="pi pi-pencil cursor-pointer"
                    title={translateText("Rename", "pt")}
                    onClick={() => {
                      setEditingId(row.id);
                      setEditingName(row.name ?? "");
                    }}
                  />
                  <i
                    className="pi pi-file-edit cursor-pointer"
                    title={translateText("Edit content", "pt")}
                    onClick={() => goEdit(row)}
                  />
                  <i
                    className="pi pi-trash cursor-pointer"
                    title={translateText("Delete", "pt")}
                    onClick={() => onDelete(row)}
                  />
                </>
              )}
            </div>
          )}
          style={{ width: "14rem" }}
        />
      </DataTable>

      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog />
      <Dialog
        visible={isCreating}
        header={translateText("Creating configuration", "pt")}
        modal
        closable={false}
        style={{ width: "32rem", maxWidth: "90vw" }}
        onHide={() => {}}
      >
        <div className="flex align-items-center gap-2 mb-3">
          <i className="pi pi-cog pi-spin text-500" />
          <span className="text-500">
            {translateText("Please wait…", "pt")}
          </span>
        </div>

        <ul className="p-0 m-0" style={{ listStyle: "none" }}>
          {steps.map((s) => (
            <li key={s.key} className="flex align-items-center gap-2 mb-2">
              <i
                className={
                  s.status === "done"
                    ? "pi pi-check-circle text-green-500"
                    : s.status === "error"
                    ? "pi pi-times-circle text-red-500"
                    : "pi pi-circle-on text-500"
                }
              />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-3">
          <ProgressBar value={progress} />
        </div>
      </Dialog>
    </div>
  );
};

export default WebsiteConfigurations;
